/**
 * Loume — Produktmenü: <lo-menue-video>
 * =====================================
 *
 * Ein kurzer Film auf einer Produktkarte im Menü. Im HTML liegt das Standbild
 * (das letzte Bild des Films); das <video> darüber ist unsichtbar und hat noch
 * keine Quelle. Ohne JavaScript, ohne IntersectionObserver und bei „Bewegung
 * reduzieren" bleibt es beim Standbild.
 *
 * Läuft das Skript, tauscht es das Standbild gegen das Startbild des Films
 * (data-startbild) — noch bevor es geladen wird, denn das Menü ist beim
 * Seitenaufruf zu und das Bild lädt „lazy". So beginnt der Film nahtlos auf
 * demselben Bild.
 *
 * Geladen wird erst, wenn die Karte zur Hälfte sichtbar ist, also das
 * Desktop-Menü aufklappt oder der Drawer hereinfährt. Dann spielt der Film
 * einmal und bleibt auf dem letzten Bild stehen. Schließt das Menü, springt er
 * an den Anfang zurück; beim nächsten Öffnen läuft er erneut.
 *
 * Warum kein IntersectionObserver allein: Den Inhalt eines geschlossenen
 * <details> blendet Chrome nur per content-visibility aus. Ob der Beobachter
 * das Schließen dann meldet, hängt vom Browser ab — deshalb hört das Element
 * zusätzlich auf „toggle" aller <details> um sich herum.
 *
 * Scheitert play() (etwa im Stromsparmodus), verschwindet das Video und das
 * Standbild kommt zurück.
 */
(function () {
  'use strict';

  if (!window.customElements || customElements.get('lo-menue-video')) return;

  var BREITEN = [400, 600, 800, 1200];

  function mitBreite(adresse, breite) {
    return adresse + (adresse.indexOf('?') === -1 ? '?' : '&') + 'width=' + breite;
  }

  class LoumeMenueVideo extends HTMLElement {
    connectedCallback() {
      if (this.beobachter) return;
      if (!('IntersectionObserver' in window)) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      var video = this.querySelector('video');
      var quelle = this.dataset.src;
      if (!video || !quelle) return;

      var self = this;
      var bild = this.querySelector('img');
      var startbild = this.dataset.startbild;

      // Das Standbild nur beim ersten Mal merken: wird das Element neu
      // eingehängt, liegt schon das Startbild darin.
      if (bild && !this.standbild) {
        this.standbild = { src: bild.getAttribute('src'), srcset: bild.getAttribute('srcset') };
      }
      var standbild = this.standbild;

      if (bild && startbild) {
        bild.setAttribute(
          'srcset',
          BREITEN.map(function (breite) {
            return mitBreite(startbild, breite) + ' ' + breite + 'w';
          }).join(', ')
        );
        bild.setAttribute('src', mitBreite(startbild, 800));
      }

      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;

      // Erst sichtbar, wenn das erste Bild da ist — bis dahin liegt darunter
      // dasselbe Startbild.
      video.addEventListener('loadeddata', function () {
        self.classList.add('ist-bereit');
      });

      // Handy-Menü „Glasblatt“ (data-blatt): eigener Ablauf, siehe unten.
      if (this.hasAttribute('data-blatt')) {
        this.imBlatt(video, bild, startbild, quelle, standbild);
        return;
      }

      var geladen = false;
      var zurueckgesetzt = true;
      var aufgegeben = false;

      function aufgeben() {
        aufgegeben = true;
        self.classList.remove('ist-bereit');
        video.removeAttribute('src');
        video.load();
        if (bild && standbild) {
          if (standbild.srcset) bild.setAttribute('srcset', standbild.srcset);
          else bild.removeAttribute('srcset');
          if (standbild.src) bild.setAttribute('src', standbild.src);
        }
        self.beobachter.disconnect();
      }

      function abspielen() {
        if (aufgegeben) return;
        if (!geladen) {
          // Poster ist das Startbild — dieselbe Datei, die das Bild darunter
          // schon geladen hat, also kein zweiter Abruf.
          if (startbild) video.poster = (bild && bild.currentSrc) || mitBreite(startbild, 800);
          video.preload = 'auto';
          video.src = quelle;
          geladen = true;
        } else if (video.currentTime > 0) {
          video.currentTime = 0;
        }
        zurueckgesetzt = false;
        var versuch = video.play();
        if (versuch && versuch.catch) {
          versuch.catch(function (fehler) {
            // AbortError: das Menü ging zu, bevor der Film lief — kein Grund aufzugeben.
            if (fehler && fehler.name === 'AbortError') return;
            aufgeben();
          });
        }
      }

      function zuruecksetzen() {
        if (!geladen || zurueckgesetzt) return;
        video.pause();
        video.currentTime = 0;
        zurueckgesetzt = true;
      }

      function versteckt() {
        return !!self.closest('details:not([open])');
      }

      this.beobachter = new IntersectionObserver(
        function (eintraege) {
          eintraege.forEach(function (eintrag) {
            if (eintrag.intersectionRatio >= 0.5 && !versteckt()) {
              if (zurueckgesetzt) abspielen();
            } else if (!eintrag.isIntersecting || versteckt()) {
              zuruecksetzen();
            }
          });
        },
        { threshold: [0, 0.5] }
      );
      this.beobachter.observe(this);

      // Öffnen und Schließen der umgebenden <details> (Produktmenü, Drawer).
      // Nach dem Öffnen neu beobachten: der Beobachter meldet dann den
      // aktuellen Stand, auch wenn sich aus seiner Sicht nichts geändert hat.
      this.umschalten = function (ereignis) {
        if (aufgegeben) return;
        if (!ereignis.target.open) {
          zuruecksetzen();
        } else {
          self.beobachter.unobserve(self);
          self.beobachter.observe(self);
        }
      };
      this.umgebung = [];
      var details = this.parentElement && this.parentElement.closest('details');
      while (details) {
        details.addEventListener('toggle', this.umschalten);
        this.umgebung.push(details);
        details = details.parentElement && details.parentElement.closest('details');
      }
    }

    /**
     * Im Handy-Menü „Glasblatt“ (snippets/loume-menue-blatt.liquid):
     * Kein Beobachter. Der Film lädt erst, wenn das Menü aufgeht (vorher
     * 0 Bytes — Bild „lazy“ im geschlossenen <details>, Video ohne Quelle),
     * und startet erst, wenn das Blatt ausgerollt ist: am Ende des
     * clip-path-Übergangs, spätestens 700 ms nach dem Öffnen (gemessen im
     * Prototyp: WebKit 619–646 ms, Chromium 583–594 ms). Sonst müsste Safari
     * zwei Glasflächen, Dimmer und zwei Filme im selben Moment rechnen. Der
     * zweite Film folgt 120 ms nach dem ersten. Schließt das Menü, springt
     * der Film an den Anfang zurück.
     */
    imBlatt(video, bild, startbild, quelle, standbild) {
      var self = this;
      var details = this.closest('details');
      var blatt = this.closest('.loume-blatt');
      if (!details || !blatt) return;
      var alle = Array.prototype.slice.call(blatt.querySelectorAll('lo-menue-video[data-blatt]'));
      var verzug = Math.max(0, alle.indexOf(this)) * 120;
      var geladen = false;
      var aufgegeben = false;
      var zeit = 0;
      var lauscher = null;

      function offen() {
        return details.open && details.classList.contains('menu-opening');
      }

      function aufgeben() {
        aufgegeben = true;
        self.classList.remove('ist-bereit');
        video.removeAttribute('src');
        video.load();
        if (bild && standbild) {
          if (standbild.srcset) bild.setAttribute('srcset', standbild.srcset);
          else bild.removeAttribute('srcset');
          if (standbild.src) bild.setAttribute('src', standbild.src);
        }
      }

      function laden() {
        if (geladen || aufgegeben) return;
        if (startbild) video.poster = (bild && bild.currentSrc) || mitBreite(startbild, 800);
        video.preload = 'auto';
        video.src = quelle;
        geladen = true;
      }

      function abspielen() {
        if (aufgegeben || !offen()) return;
        if (video.currentTime > 0) video.currentTime = 0;
        var versuch = video.play();
        if (versuch && versuch.catch) {
          versuch.catch(function (fehler) {
            if (fehler && fehler.name === 'AbortError') return;
            aufgeben();
          });
        }
      }

      function aufraeumen() {
        clearTimeout(zeit);
        if (lauscher) blatt.removeEventListener('transitionend', lauscher);
        lauscher = null;
      }

      function nachDemAufrollen() {
        aufraeumen();
        var los = function () {
          aufraeumen();
          if (!offen()) return;
          zeit = setTimeout(abspielen, verzug);
        };
        lauscher = function (ereignis) {
          if (ereignis.target === blatt && ereignis.propertyName === 'clip-path') los();
        };
        blatt.addEventListener('transitionend', lauscher);
        zeit = setTimeout(los, 700);
      }

      function zuruecksetzen() {
        aufraeumen();
        if (!geladen || aufgegeben) return;
        video.pause();
        try {
          video.currentTime = 0;
        } catch (e) {
          /* noch nicht bereit */
        }
      }

      this.umschalten = function () {
        if (aufgegeben) return;
        if (details.open) {
          laden();
          nachDemAufrollen();
        } else {
          zuruecksetzen();
        }
      };
      this.umgebung = [details];
      details.addEventListener('toggle', this.umschalten);
      this.beobachter = { disconnect: aufraeumen };
      // Ging das Menü schon auf, bevor das Skript da war: jetzt nachholen.
      if (details.open) this.umschalten();
    }

    disconnectedCallback() {
      if (this.beobachter) this.beobachter.disconnect();
      var self = this;
      (this.umgebung || []).forEach(function (details) {
        details.removeEventListener('toggle', self.umschalten);
      });
      this.beobachter = null;
    }
  }

  customElements.define('lo-menue-video', LoumeMenueVideo);
})();
